import React, { useState } from 'react';
import './add-subaccount.css';
import { useInvestmentData } from '../lib/useInvestmentData.js';

// Stub out the tweaks panel — designer-only tool, no production presence.
const TweaksPanel = ({ children }) => null;
const TweakSection = ({ children }) => null;
const TweakRadio = () => null;
const TweakSelect = () => null;
const TweakColor = () => null;
const TweakSlider = () => null;
const TweakText = () => null;
const TweakToggle = () => null;
const TweakButton = () => null;
const useTweaks = (defaults) => [defaults, () => {}];



// Full avatar palette — the 4 "new-member" colors from the design handoff
// PLUS the 4 colors that used to be reserved for the demo family. Now
// that the household-data is real, those are no longer "taken" by anyone
// and should be selectable.
const PALETTE = [
  { id: 'emerald', name: 'Emerald', color: '#10b981' },
  { id: 'violet',  name: 'Violet',  color: '#8b5cf6' },
  { id: 'coral',   name: 'Coral',   color: '#f97316' },
  { id: 'slate',   name: 'Slate',   color: '#64748b' },
  { id: 'indigo',  name: 'Indigo',  color: '#4f46e5' },
  { id: 'sky',     name: 'Sky',     color: '#0ea5e9' },
  { id: 'amber',   name: 'Amber',   color: '#f59e0b' },
  { id: 'pink',    name: 'Pink',    color: '#ec4899' },
];

// ─── Helpers ───
function shade(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  const cl = (x) => Math.max(0, Math.min(255, x));
  return '#' + (0x1000000 + cl(R) * 0x10000 + cl(G) * 0x100 + cl(B)).toString(16).slice(1);
}
// ─── App chrome ───


const Breadcrumb = ({ holderName }) => (
  <nav className="breadcrumb" aria-label="Breadcrumb">
    <a href="Investment Home.html">Investment</a>
    <span className="bc-sep">›</span>
    <a href="Investment Home.html">{holderName ? `${holderName}'s family` : 'Family'}</a>
    <span className="bc-sep">›</span>
    <span className="bc-current">Add sub-account</span>
  </nav>
);

// ─── Identity (the only form step — T-012 removed the Allocation step,
// and T-043 retired the allocations concept altogether) ───
// `taken` is an array of { color, by } for existing family members so the
// swatch row can dim/label colors already in use. Pass an empty array when
// the account holder has no sub-accounts yet — the divider + taken row
// will just collapse.
// `existingNames` is currently unused (was for "Same set as X/Y/Z" copy on
// the now-removed account-provisioning toggles) but kept on the prop list
// so the caller's wiring stays stable for future identity-related copy.
const Step1Identity = ({ name, setName, color, setColor, taken = [], existingNames = [] }) => { // eslint-disable-line no-unused-vars
  const colorDark = shade(color, -22);
  const initial = (name.trim()[0] || '?').toUpperCase();

  return (
    <div data-screen-label="01 Identity">
      <div className="field-group">
        <label className="field-label">Identity</label>
        <div className="identity-row">
          <input
            className="text-input large"
            placeholder="Sub-account name (e.g. Omar)"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
          <div className="preview-avatar" style={{'--member-color': color, '--member-color-dark': colorDark}}>
            {initial}
          </div>
        </div>
        <div className="field-help">
          Creates a <span className="mono">members</span> row managed by you. No login or profile is created.
        </div>
      </div>

      <div className="field-group">
        <label className="field-label">Avatar color</label>
        <div className="swatch-row">
          {PALETTE.map(p => (
            <button
              key={p.id}
              className={'swatch' + (color === p.color ? ' selected' : '')}
              style={{'--sw-color': p.color}}
              onClick={() => setColor(p.color)}
              title={p.name}
            >
              {color === p.color && (
                <span className="swatch-check">
                  <svg viewBox="0 0 14 14" width="14" height="14"><path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              )}
            </button>
          ))}
          {taken.length > 0 && <div className="swatch-divider" aria-hidden="true"></div>}
          {taken.map(t => (
            <div key={t.color + '-' + t.by}
              className="swatch taken"
              style={{'--sw-color': t.color}}
              data-by={t.by}
              title={t.by + ' uses this'}
            ></div>
          ))}
        </div>
        <div className="field-help" style={{marginTop: 10}}>
          {taken.length > 0
            ? <>Members already using colors are dimmed. Pick something distinct for the family grid.</>
            : <>No other members yet — any color works.</>}
        </div>
      </div>

      {/*
        Removed: Notes textarea + "Financial accounts to provision" toggles.
        Both were unsaved cosmetic state — Notes had no corresponding
        members column, and the bank toggles never wrote to
        financial_accounts (real bank rows are created from the Executors
        flow, not from this form). Keeping them would have implied
        persistence that didn't exist.
      */}
    </div>
  );
};

// Family preview rail — shows the new member sliding into the grid.
// (T-043: the per-member allocation figures are gone with the concept —
// the tiles show identity only.)
const FamilyPreview = ({ name, color, family = [] }) => {
  const initial = (name.trim()[0] || '?').toUpperCase();
  return (
    <div className="family-preview">
      <div className="fp-head">Preview · family grid after save</div>
      <div className="fp-tiles">
        {family.map(m => (
          <div key={m.id} className="fp-tile" style={{'--member-color': m.color}}>
            <div className="fp-avatar" style={{background: 'linear-gradient(135deg,' + m.color + ',' + shade(m.color, -22) + ')'}}>
              {m.initials}
            </div>
            <div className="fp-meta">
              <div className="fp-name">{m.name}</div>
              <div className="fp-alloc mono">{m.role === 'holder' ? 'Holder' : 'Sub'}</div>
            </div>
          </div>
        ))}
        <div className="fp-tile fp-new" style={{'--member-color': color}}>
          <span className="fp-new-pill">New</span>
          <div className="fp-avatar" style={{background: 'linear-gradient(135deg,' + color + ',' + shade(color, -22) + ')'}}>
            {initial}
          </div>
          <div className="fp-meta">
            <div className="fp-name">{name.trim() || '—'}</div>
            <div className="fp-alloc mono">Sub</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Success ───
const Success = ({ name, color, holderName = 'you', onAddAnother, doneHref = '#home', doneLabel = 'View on dashboard →' }) => {
  const initial = (name.trim()[0] || '?').toUpperCase();
  return (
    <div className="success-wrap" data-screen-label="03 Success">
      <div className="success-burst">
        <div className="success-ring-2"></div>
        <div className="success-ring-1"></div>
        <div className="success-avatar" style={{'--member-color': color, '--member-color-dark': shade(color, -22)}}>
          {initial}
        </div>
        <div className="success-check">
          <svg viewBox="0 0 14 14" width="14" height="14"><path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
      </div>

      <h2 className="success-title">{name.trim() || 'New sub-account'} added to the family</h2>
      <p className="success-sub">
        Sub-account row created under {holderName}. They're on the household's loops right away.
      </p>

      <div className="success-recap">
        <div className="success-recap-eye">Configuration</div>
        <SuccessRow
          icon="✓"
          text={<>Managed member under {holderName} — shares the household hurdle and split</>}
        />
        <SuccessRow
          icon="✓"
          text={<>Their movements log against the household's loops right away</>}
        />
      </div>

      <div className="success-actions">
        <button type="button" className="btn-ghost" onClick={onAddAnother}>+ Add another</button>
        <a className="btn-primary" href={doneHref}>{doneLabel}</a>
      </div>
    </div>
  );
};

const SuccessRow = ({ icon, text }) => (
  <div className="success-recap-row">
    <div className="success-recap-icon">
      <svg viewBox="0 0 14 14" width="13" height="13"><path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </div>
    <div className="success-recap-text">{text}</div>
  </div>
);

// ─── App root ───
function AddSubaccount({ member, profile, params } = {}) {
  const [step, setStep] = useState(1);   // 1 (identity) | 'success'

  // Real household + loop data via the shared hook. While loading, child
  // components receive empty defaults so the form is usable but the right
  // rail shows just the new-member tile.
  const { data: invData } = useInvestmentData(member, profile);
  const family = invData?.members || [];
  const subAccounts = family.filter(m => m.role === 'sub');
  const takenColors = family.map(m => ({ color: m.color, by: m.name }));
  const holderName = invData?.accountHolder?.name || member?.display_name || member?.name || 'you';
  const hurdle = Number(invData?.accountHolder?.hurdle || 0);
  const [name, setName]           = useState('');
  const [color, setColor]         = useState('#10b981');
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState(null);

  // When the admin opened this screen with a target holder in
  // params.memberId (the T-011 direct-admin path), Back/Cancel/Done return
  // via the retired drill-down hash — which now redirects into that
  // member's investor view (T-042). The normal act-as path carries no
  // memberId and just goes Home.
  const adminReturn = params?.memberId
    ? '#admin-member-detail?memberId=' + encodeURIComponent(params.memberId)
    : null;
  const backHref = adminReturn || '#home';

  const canSave = name.trim().length > 0;

  // T-012: identity → save → done — the save writes ONLY the members row.
  // (T-043: allocations are gone entirely; assignment decides loop access.)
  const save = async () => {
    // Real Supabase insert. RLS: the holder path is covered by "Holder adds
    // sub-account" (managed_by_member_id = get_my_member_id()); the direct
    // admin path (T-011) is covered by the admin write policy — the same
    // authorization act-as already relies on.
    setSaving(true);
    setSaveError(null);
    try {
      const sb = (typeof window !== 'undefined') ? window.sb : null;
      if (!sb) throw new Error('Supabase client unavailable');
      if (!member?.id) throw new Error('No account holder context');
      // Slug from name; collision-safe via short random suffix.
      const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'sub';
      const id = slug + '-' + Math.random().toString(36).slice(2, 8);
      const { error } = await sb.from('members').insert({
        id,
        name: name.trim(),
        display_name: name.trim(),
        managed_by_member_id: member.id,
        active: true,
        // Persist the picked avatar color so the bubble matches the picker
        // (the read-side falls back to a hash of id when this is NULL).
        color,
      });
      if (error) throw error;
      setStep('success');
    } catch (e) {
      setSaveError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const addAnother = () => {
    setName('');
    setColor('#10b981');
    setSaveError(null);
    setStep(1);
  };

  return (
    <div className="app">
      <main className="page">
        <Breadcrumb holderName={holderName} />

        <div className="page-title-row">
          <div>
            <div className="page-eyebrow">Investment module · Family</div>
            <h1 className="page-title">
              {step === 'success' ? 'Sub-account added' : 'Add a sub-account'}
            </h1>
            <p className="page-sub">
              {step === 'success'
                ? <>{name.trim() || 'New member'} now appears under {holderName} — they share the household hurdle and 50/50 split.</>
                : <>A managed member of {holderName}'s family. Shares the household hurdle and gets their own ledger.</>}
            </p>
          </div>
          <a className="btn-ghost" href={backHref} style={{ flexShrink: 0 }}>← Back</a>
        </div>

        {step === 'success' ? (
          <div className="success-grid">
            <Success
              name={name} color={color}
              holderName={holderName}
              onAddAnother={addAnother}
              doneHref={backHref}
              doneLabel={adminReturn ? 'Open their investor view →' : 'View on dashboard →'}
            />
            <FamilyPreview name={name} color={color} family={family} />
          </div>
        ) : (
          <div className="form-grid">
            <section className="form-main card">
              <Step1Identity
                name={name} setName={setName}
                color={color} setColor={setColor}
                taken={takenColors}
                existingNames={subAccounts.map(m => m.name)}
              />
            </section>
            <aside className="form-rail">
              <FamilyPreview name={name} color={color} family={family} />
              <div className="info-card">
                <div className="info-card-eye">About sub-accounts</div>
                <ul className="info-list">
                  <li>Managed by <strong>you</strong> — no login or profile is created.</li>
                  <li>Shares the household {hurdle > 0 ? <><span className="mono">${hurdle.toLocaleString()}</span> hurdle and </> : null}<strong>50/50</strong> split after.</li>
                  <li>Tracked as its own <span className="mono">members</span> row with <span className="mono">managed_by_member_id</span>.</li>
                  <li>On the household's loops right away — rename or deactivate any time from their tile.</li>
                </ul>
              </div>
            </aside>
          </div>
        )}

        {step !== 'success' && (
          <>
            {saveError && (
              <div style={{ background: 'var(--rose-bg)', border: '1px solid var(--rose-border)', color: 'var(--rose)', padding: '10px 14px', borderRadius: 'var(--radius)', marginTop: 12, fontSize: 13 }}>
                <strong>Couldn't add member:</strong> {saveError}
              </div>
            )}
            <footer className="page-footer-actions">
              <div className="pfa-left">
                <a className="btn-link" href={backHref}>← Cancel</a>
              </div>
              <div className="pfa-right">
                <button
                  className="btn-primary lg"
                  onClick={save}
                  disabled={!canSave || saving}
                  aria-disabled={!canSave || saving}
                >
                  {saving ? 'Saving…' : <>Add {name.trim() || 'member'} <span aria-hidden>✓</span></>}
                </button>
              </div>
            </footer>
          </>
        )}
      </main>
    </div>
  );
}

export default AddSubaccount;
